import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import {
  useGetPhotoNumberQuery,
  type PhotoNumberQueryArg,
} from '@/api/enterance'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useDispatch, useSelector } from 'react-redux'
import { 
  clearPlateNumberHistoryParams, 
  selectPlateNumberHistoryParams, 
  selectShowPlateNumberHistory, 
} from '../modalsSlice'
import CloseIcon from '@mui/icons-material/Close';
import PlateNumberHistoryContent from './PlateNumberHistoryContent'
import { usePermissions } from '@/hooks/usePermissions'
import HistoryTypeSelector from './HistoryTypeSelector'
import { EditDebtModalContent } from '@/features/billing'

const historyTypeList = [
  {
    value: 'entries',
    translationKey: 'entries',
  },
  {
    value: 'debts',
    translationKey: 'debts',
  },
]

const PlateNumberHistoryModal = () => {
  const [historyType, setHistoryType] = useState(historyTypeList[0].value);
  const { hasPermission } = usePermissions();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const show = useSelector(selectShowPlateNumberHistory);
  const modalParams = useSelector(selectPlateNumberHistoryParams);
  const [lightboxEntryId, setLightboxEntryId] = useState<
    PhotoNumberQueryArg | undefined
  >(undefined);

  const onClose = () => {
    setHistoryType(historyTypeList[0].value);
    dispatch(clearPlateNumberHistoryParams())
  }

  return (
    <>
      <Dialog
        open={show}
        onClose={onClose}
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              minHeight: '50vh',
              minWidth: {
                md: '600px'
              }
            }
          }
        }}
      >
        
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            gap: 4,
            m: {
              xs: 1,
              md: 2,
            },
            mx: 2,
          }}
        >
          {
            hasPermission('billing.writeOff') 
              ? <HistoryTypeSelector 
                  value={historyType} 
                  onChange={(value) => setHistoryType(value)} 
                  list={historyTypeList} 
                /> 
              : <DialogTitle id="scroll-dialog-title">{t('history')}</DialogTitle> 
          }
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {
          historyType === historyTypeList[0].value && (
            <PlateNumberHistoryContent onPhotoClick={(id) => setLightboxEntryId(id)} />
          )
        }
        {
          historyType === historyTypeList[1].value && (
            <EditDebtModalContent 
              queryParams={modalParams}
              sx={{
                mx: {
                  xs: 1,
                  md: 2
                }, 
              }}
            />
          )
        }
      </Dialog>
      <PlateNumberPhotoLightbox 
        entryId={lightboxEntryId}
        onClose={() => setLightboxEntryId(undefined)}
      />
    </>
  )
}

interface PlateNumberPhotoLightboxProps {
  entryId: PhotoNumberQueryArg | undefined
  onClose: () => void
}

const PlateNumberPhotoLightbox = ({
  entryId,
  onClose
}: PlateNumberPhotoLightboxProps) => {
  const{ 
    data, 
    isLoading, 
    isSuccess, 
    isFetching: photoFetching 
  } = useGetPhotoNumberQuery(entryId ?? skipToken);
  
  return (
    <Backdrop
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 200 }}
      open={!!entryId}
      onClick={onClose}
    >
      {(isLoading || photoFetching) && <CircularProgress color="inherit" />}
      {!photoFetching && isSuccess && (
        <Box
          component="img"
          sx={{ minWidth: 250, maxHeight: 300 }}
          src={`data:image/png;base64,${data?.image_base_64}`}
        />
      )}
    </Backdrop>
  )
}

export default PlateNumberHistoryModal;
